import { useEffect, useRef, useState } from "react";
import { useGameStore } from "../../store/gameStore";

function RollingDie({ sides, finalValue, delayMs = 0 }) {
  const [display, setDisplay] = useState(finalValue);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    setSettled(false);
    const startTimeout = setTimeout(() => {
      let ticks = 0;
      const maxTicks = 12;
      const interval = setInterval(() => {
        ticks++;
        if (ticks >= maxTicks) {
          setDisplay(finalValue);
          setSettled(true);
          clearInterval(interval);
        } else {
          setDisplay(1 + Math.floor(Math.random() * sides));
        }
      }, 55);
      return () => clearInterval(interval);
    }, delayMs);
    return () => clearTimeout(startTimeout);
  }, [finalValue, sides, delayMs]);

  return (
    <div
      data-testid={settled ? "rolling-die-settled" : "rolling-die-spinning"}
      className={`w-10 h-10 rounded-lg bg-gradient-to-br from-[#2a2426] to-[#322216] border-2 flex items-center justify-center font-display text-base shrink-0 transition-all duration-150 ${
        settled ? "border-[#D4AF37] scale-110 shadow-[0_0_14px_rgba(212,175,55,0.55)] text-[#D4AF37]" : "border-white/15 text-[#f1e0c5]/70"
      }`}
    >
      {display}
    </div>
  );
}

function RollEventCard({ event, onDone }) {
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), 3200);
    const removeTimer = setTimeout(() => onDone(event.id), 3600);
    return () => { clearTimeout(leaveTimer); clearTimeout(removeTimer); };
  }, [event.id, onDone]);

  return (
    <div
      className={`dice-roll-card flex items-center gap-2.5 px-3 py-2 rounded-2xl bg-black/75 backdrop-blur-xl border border-white/10 ${leaving ? "dice-roll-leaving" : ""}`}
      data-testid={`dice-animation-${event.id}`}
    >
      <div className="flex gap-1">
        {event.dice.map((d, i) => <RollingDie key={i} sides={d.sides} finalValue={d.value} delayMs={i * 90} />)}
      </div>
      <div className="text-xs leading-tight">
        <div className="text-[#D4AF37]/80 font-semibold truncate max-w-[140px]">{event.label}</div>
        <div className="text-[#f1e0c5]/60">
          {event.modifierText} = <span className="text-white font-bold font-mono-sheet">{event.total}</span>
        </div>
      </div>
    </div>
  );
}

/** Floating 2D dice-roll animation: whenever a fresh roll arrives in chat (public, GM-only, or a whisper
 * the current viewer is authorized to see — the backend already filters who receives which roll), a
 * small die (or one per individual die in a damage formula) pops in at the bottom of the screen, cycles
 * through random faces, and settles on the real result. Multiple simultaneous rolls stack vertically. */
export default function DiceAnimationOverlay() {
  const chatMessages = useGameStore((s) => s.chatMessages);
  const [events, setEvents] = useState([]);
  const processedIds = useRef(new Set());

  useEffect(() => {
    if (!chatMessages?.length) return;
    const now = Date.now();
    const fresh = [];
    for (const m of chatMessages) {
      if (processedIds.current.has(m.id)) continue;
      processedIds.current.add(m.id);
      if (!m.roll_breakdown || m.roll_breakdown.manual) continue;
      const age = now - new Date(m.created_at).getTime();
      if (age >= 0 && age < 4000) fresh.push(m);
    }
    if (!fresh.length) return;

    const newEvents = fresh.map((m) => {
      const b = m.roll_breakdown;
      let dice, modifierText;
      if (b.die !== undefined) {
        dice = (b.rolls || [b.die]).map((v) => ({ sides: 20, value: v }));
        modifierText = `d20${b.modifier >= 0 ? "+" : ""}${b.modifier || 0}`;
      } else {
        dice = (b.rolls || []).map((v) => ({ sides: b.die_sides || 6, value: v }));
        modifierText = b.formula || "";
      }
      return { id: m.id, label: m.sender_name ? `${m.sender_name} rolls ${m.content || ""}`.trim() : "Roll", dice, modifierText, total: b.total };
    });
    setEvents((prev) => [...prev, ...newEvents]);
  }, [chatMessages]);

  const removeEvent = (id) => setEvents((prev) => prev.filter((e) => e.id !== id));

  if (!events.length) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col-reverse gap-2 items-center pointer-events-none" data-testid="dice-animation-overlay">
      {events.map((event) => <RollEventCard key={event.id} event={event} onDone={removeEvent} />)}
    </div>
  );
}
